import React from "react";
import { Modal, Badge, Spinner } from "react-bootstrap";
import { SmallButton } from "@rpforms/shared/build/components/layout/Button";

interface FormEntryDetailsModalProps {
    formEntry: any;
    show: boolean;
    onHide: () => void;
    loading?: boolean;
}

export const FormEntryDetailsModal: React.FC<FormEntryDetailsModalProps> = ({
    formEntry,
    show,
    onHide,
    loading = false
}) => {
    if (!formEntry && !loading) return null;
    
    const getStateDisplay = (state: string) => {
        const stateMap: { [key: string]: { label: string; variant: "secondary" | "primary" | "info" | "success" | "danger" | "warning" | "dark" | "light" } } = {
            'initialized': { label: 'Initialisiert', variant: 'secondary' },
            'open': { label: 'Offen', variant: 'primary' },
            'processed': { label: 'Verarbeitet', variant: 'info' },
            'accepted': { label: 'Akzeptiert', variant: 'success' },
            'rejected': { label: 'Abgelehnt', variant: 'danger' },
            'paid': { label: 'Bezahlt', variant: 'success' },
            'reminded': { label: 'Erinnert', variant: 'warning' },
            'canceled': { label: 'Storniert', variant: 'dark' },
            'offer_needed': { label: 'Angebot erforderlich', variant: 'warning' },
            'voucher': { label: 'Gutschein', variant: 'info' }
        };
        const stateInfo = stateMap[state] || { label: state, variant: 'secondary' as const };
        return <Badge variant={stateInfo.variant}>{stateInfo.label}</Badge>;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount / 100);
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Protokoll Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status">
                            <span className="sr-only">Laden...</span>
                        </Spinner>
                        <div className="mt-2">Details werden geladen...</div>
                    </div>
                ) : formEntry ? (
                    <>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <strong>Name:</strong> {formEntry.name}
                            </div>
                            <div className="col-md-6">
                                <strong>Status:</strong> {getStateDisplay(formEntry.state)}
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <strong>Erstellt am:</strong> {formatDate(formEntry.created_at)}
                            </div>
                            {formEntry.dated_at && (
                                <div className="col-md-6">
                                    <strong>Datum:</strong> {formatDate(formEntry.dated_at)}
                                </div>
                            )}
                        </div>

                        {formEntry.meta?.summary && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <strong>Zusammenfassung:</strong>
                                    <div className="mt-1 p-2 bg-light border rounded">
                                        {formEntry.meta.summary}
                                    </div>
                                </div>
                            </div>
                        )}
                        {formEntry.meta?.total_sales_price !== null && formEntry.meta?.total_sales_price !== undefined && (
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <strong>Gesamtpreis:</strong> {formatCurrency(formEntry.meta.total_sales_price)}
                                </div>
                            </div>
                        )}
                        {formEntry.customer && (
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <strong>Kunde:</strong> {formEntry.customer.name}
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </Modal.Body>
            <Modal.Footer>
                <SmallButton onClick={onHide}>
                    Schließen
                </SmallButton>
            </Modal.Footer>
        </Modal>
    );
};
